import { useMemo, FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { useStore } from '@hooks/useStore';
import { useMyQuery } from '@hooks/useMyQuery';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useFilterEntity } from '@hooks/useFilterEntity';
import Table from '@components/Core/Table/Table';
import { EntityIcon } from '@components/Core/EntityIcon/EntityIcon';
import { StatusBadge } from '@components/Core/StatusBadge';
import Popup from '@components/Core/Popup/Popup';
import { NoResults } from '@components/Core/NoResults';
import { AppLoader } from '@components/Core/AppLoader';
import { Button } from '@components/Core/Button';
import { StatusPopup } from '@popups/StatusPopup';
import { StatusPopupProps } from '@popups/StatusPopup/types';
import SettingsIcon from '@icons/settings_icon.svg?component';
import { IBusinessBrandData } from '@stores/brands/types';
import { BrandsTransFields } from '@pages/Brands/types';
import { brandsTranslations } from '@pages/Brands/translations';
import { tabsToUrl } from '@components/Core/BrandTabs/constants';
import { BrandTabType } from '@components/Core/BrandTabs/types';
import { BrandsTableTransFields } from './types';
import { brandsTableTranslations } from './translations';

const columnHelper = createColumnHelper<IBusinessBrandData>();

export const BrandsTable: FC<{ search: string }> = observer(({ search }) => {
  const {
    brandsStore: { brandsList, getBrandsList, setBrand },
    mainStore: { selectedCountryCode },
    popupStore: { openPopup, popupName },
  } = useStore();

  const [popupData, setPopupData] = useState<StatusPopupProps | null>(null);

  const { isFetching, refetch } = useMyQuery({
    queryKey: ['brands'],
    queryFn: () => getBrandsList(),
    enabled: !!selectedCountryCode, // wait until it's defined
  });

  useEffect(() => {
    if (selectedCountryCode?.countryCode) refetch();
  }, [selectedCountryCode?.countryCode]);

  const { t } = useMyTranslations<BrandsTableTransFields | Extract<BrandsTransFields, 'addBrand'>>([
    brandsTableTranslations.name,
    brandsTranslations.name,
  ]);

  const data = useFilterEntity(brandsList, search);

  const navigate = useNavigate();

  const onClick = useCallback((brand: IBusinessBrandData, tab: BrandTabType = 'edit') => {
    const { id, status, adminMessage } = brand;
    if (status === 'Declined' && adminMessage) {
      setPopupData({ status, text: adminMessage });
      openPopup('status');
      return;
    }
    if (status === 'Pending') {
      setPopupData({ status, text: adminMessage });
      openPopup('status');
      return;
    }
    if (status === 'Revise') {
      setBrand(brand);
      navigate(`${id}`);
      return;
    }
    setBrand(brand);
    navigate(`${id}${tabsToUrl[tab]}`);
  }, []);

  const columns: ColumnDef<IBusinessBrandData, any>[] = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: t('name'),
        cell: ({
          row: {
            original,
            original: { logoUrl, name },
          },
        }) => (
          <button
            className="flex items-center w-full gap-x-[12px] bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching}
          >
            <EntityIcon src={logoUrl} name="brand image" />
            <p className="whitespace-nowrap overflow-hidden text-ellipsis">{name || '—'}</p>
          </button>
        ),
        enableSorting: true,
        meta: { grow: true },
      }),
      columnHelper.accessor('pointsCount', {
        id: 'pointsCount',
        header: t('pointsCount'),
        cell: ({
          row: {
            original,
            original: { pointsCount },
          },
        }) => (
          <button
            className="w-full bg-none cursor-pointer"
            onClick={() => onClick(original, 'point list')}
            disabled={isFetching}
          >
            <p className="text-left">{pointsCount}</p>
          </button>
        ),
        enableSorting: true,
        meta: { width: 64, className: 'hide-on-mobile' },
      }),
      columnHelper.accessor('productsCount', {
        id: 'productsCount',
        header: t('productsCount'),
        cell: ({
          row: {
            original,
            original: { productsCount },
          },
        }) => (
          <button
            className="w-full bg-none cursor-pointer"
            onClick={() => onClick(original, 'product list')}
            disabled={isFetching}
          >
            <p className="text-left">{productsCount}</p>
          </button>
        ),
        enableSorting: true,
        meta: { width: 85, className: 'hide-on-mobile' },
      }),
      // TODO uncommented after feature will be unfrosted
      // columnHelper.accessor('cataloguesCount', {
      //   id: 'cataloguesCount',
      //   header: t('cataloguesCount'),
      //   cell: ({
      //     row: {
      //       original,
      //       original: { cataloguesCount },
      //     },
      //   }) => (
      //     <button
      //       className="w-full bg-none cursor-pointer"
      //       onClick={() => onClick(original, 'catalogue list')}
      //       disabled={isFetching}
      //     >
      //       <p className="text-left">{cataloguesCount || 0}</p>
      //     </button>
      //   ),
      //   enableSorting: true,
      //   meta: { width: 85, className: 'hide-on-mobile' },
      // }),
      columnHelper.accessor('status', {
        id: 'status',
        header: t('status'),
        cell: ({
          row: {
            original,
            original: { status },
          },
        }) => (
          <button
            className="w-full h-full bg-none cursor-pointer"
            onClick={() => onClick(original)}
            disabled={isFetching}
          >
            <StatusBadge status={status} />
          </button>
        ),
        enableSorting: true,
        meta: { width: 80 },
      }),
      {
        id: 'option',
        header: t('option'),
        cell: ({ row: { original } }) => (
          <button
            className="w-full h-full bg-none cursor-pointer flex-centred"
            onClick={() => onClick(original)}
            disabled={isFetching}
          >
            <SettingsIcon height={24} width={24} />
          </button>
        ),
        enableSorting: false,
        meta: { width: 56 },
      },
    ],
    [isFetching]
  );
  if (isFetching) return <AppLoader />;
  if ((data.length === 0 && search) || brandsList?.length === 0)
    return (
      <NoResults>
        <p className="small-text">{t('noResultText')}</p>
        <Button
          text={t('addBrand', { ns: brandsTranslations.name })}
          onClick={() => navigate('/add-brand')}
          styleProp="max-w-[153px]"
        />
      </NoResults>
    );
  return (
    <>
      <Table columns={columns} data={data} />
      <Popup>{popupName === 'status' && popupData && <StatusPopup {...popupData} />}</Popup>
    </>
  );
});
